/** One queue and one owner of blob URLs for history and realtime, per browser tab. */
export type MediaLease = { promise: Promise<string>; release: () => void }
type Job = {
  key: string
  priority: number
  consumers: Set<symbol>
  controller: AbortController
  load: (signal: AbortSignal) => Promise<Blob>
  promise: Promise<string>
  resolve: (url: string) => void
  reject: (error: unknown) => void
  url?: string
  bytes: number
  touched: number
  running: boolean
}

export class MediaResolver {
  private jobs = new Map<string, Job>()
  private active = 0
  constructor(
    private concurrency = 3,
    private ttl = 15 * 60_000,
    private budget = 100 * 1024 * 1024,
    private timeout = 30_000,
  ) {}

  acquire(key: string, load: Job['load'], priority = 1): MediaLease {
    this.sweep()
    let job = this.jobs.get(key)
    if (!job) {
      let resolve!: Job['resolve']
      let reject!: Job['reject']
      const promise = new Promise<string>((res, rej) => {
        resolve = res
        reject = rej
      })
      job = {
        key,
        priority,
        consumers: new Set(),
        controller: new AbortController(),
        load,
        promise,
        resolve,
        reject,
        bytes: 0,
        touched: Date.now(),
        running: false,
      }
      this.jobs.set(key, job)
    }
    job.priority = Math.min(job.priority, priority)
    const token = Symbol(key)
    job.consumers.add(token)
    job.touched = Date.now()
    this.pump()
    let released = false
    return {
      promise: job.promise,
      release: () => {
        if (released) return
        released = true
        job!.consumers.delete(token)
        job!.touched = Date.now()
        if (!job!.consumers.size && !job!.url) {
          job!.controller.abort()
          job!.reject(new DOMException('Consumer cancelled', 'AbortError'))
          if (this.jobs.get(key) === job) this.jobs.delete(key)
        }
        this.sweep()
      },
    }
  }

  private pump() {
    while (this.active < this.concurrency) {
      const job = [...this.jobs.values()]
        .filter((j) => !j.running && !j.url && j.consumers.size)
        .sort((a, b) => a.priority - b.priority || a.touched - b.touched)[0]
      if (!job) return
      job.running = true
      this.active++
      void this.run(job)
    }
  }

  private async run(job: Job) {
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          job.controller.abort()
          reject(new Error('Tempo limite do anexo'))
        }, this.timeout)
      })
      const blob = await Promise.race([job.load(job.controller.signal), timeout])
      if (job.controller.signal.aborted || !job.consumers.size) throw new DOMException('Cancelled', 'AbortError')
      if (!blob.size || blob.type.toLowerCase().includes('text/html')) throw new Error('Resposta de mídia inválida')
      job.url = URL.createObjectURL(blob)
      job.bytes = blob.size
      job.resolve(job.url)
    } catch (error) {
      if (this.jobs.get(job.key) === job) this.jobs.delete(job.key)
      job.reject(error)
    } finally {
      clearTimeout(timer)
      this.active--
      this.pump()
    }
  }

  sweep(now = Date.now()) {
    const unused = [...this.jobs.values()]
      .filter((j) => j.url && !j.consumers.size)
      .sort((a, b) => a.touched - b.touched)
    let bytes = unused.reduce((total, job) => total + job.bytes, 0)
    for (const job of unused) {
      if (now - job.touched < this.ttl && bytes <= this.budget) continue
      URL.revokeObjectURL(job.url!)
      bytes -= job.bytes
      this.jobs.delete(job.key)
    }
  }

  get stats() {
    return {
      active: this.active,
      entries: this.jobs.size,
      bytes: [...this.jobs.values()].reduce((total, job) => total + job.bytes, 0),
    }
  }
}

export const mediaResolver = new MediaResolver()
